import React, { memo, useCallback, useEffect, useMemo } from 'react';

import { Typography } from '@mui/material';
import { DateFieldCDS } from '@sharedCR/coreDesignSystem/form';
import ModalFormWrapper from 'app/shared/componentsRestyled/coreDesignSystem/ModalFormWrapper';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import useForm from 'hooks/useForm';

import { CONFIRMATION_NOTE, LIQUIDATION_DATE_HELP } from './MersDeactivationDetailsForm.constants';
import { MersDeactivationDetailsFormTestIds } from './MersDeactivationDetailsForm.testIds';
import {
  buildMersDeactivationDetails,
  getLiquidationMaxDate,
  getMersDeactivationDetailsSchema,
} from './MersDeactivationDetailsForm.utils';

const MersDeactivationDetailsForm = ({ kind, isLoading, setIsLoading, formRef, onSubmit }) => {
  const { form, setSchema, setInForm, validateForm, validateField, validationErrors } = useForm({});

  const liquidationMaxDate = useMemo(() => getLiquidationMaxDate(), []);

  const onDateFieldChange = useCallback(
    (value, name) => {
      setInForm(name, value);
      validateField(name, value);
    },
    [setInForm, validateField],
  );

  const onSubmitForm = useCallback(
    async e => {
      e?.preventDefault?.();

      if (!(await validateForm({ noFormChangedValidate: true }))) {
        return;
      }

      setIsLoading(true);
      onSubmit(buildMersDeactivationDetails(form)).finally(() => setIsLoading(false));
    },
    [validateForm, form, onSubmit, setIsLoading],
  );

  useEffect(() => {
    setSchema(getMersDeactivationDetailsSchema());
  }, [setSchema]);

  return (
    <ModalFormWrapper
      isFormChanged
      formRef={formRef}
      formTestId={MersDeactivationDetailsFormTestIds.FORM}
      isSubmitting={isLoading}
      submitButtonName="Send to MERS"
      submitButtonTestId={MersDeactivationDetailsFormTestIds.SUBMIT_BUTTON}
      onSubmit={onSubmitForm}
    >
      <Typography className="mb-16" color="text.secondary" component="p" variant="regular14">
        {`${LIQUIDATION_DATE_HELP[kind]} ${CONFIRMATION_NOTE}`}
      </Typography>
      <DateFieldCDS
        required
        dataTestId={MersDeactivationDetailsFormTestIds.LIQUIDATION_DATE}
        disabled={isLoading}
        error={!!validationErrors.liquidationDate}
        helperText={validationErrors.liquidationDate}
        label="Liquidation date"
        maxDate={liquidationMaxDate}
        name="liquidationDate"
        value={form.liquidationDate || null}
        onChange={onDateFieldChange}
      />
    </ModalFormWrapper>
  );
};

MersDeactivationDetailsForm.propTypes = {
  ...WithLoadingPropTypes,
  kind: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default compose(withLoading, memo)(MersDeactivationDetailsForm);
